
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginComponent from '@/components/login-component';
import { useAuth } from '@/context/AuthContext';
import { ChefHat } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/ui-elements/language-switcher';
import { useAppSettings } from '@/hooks/useAppSettings';
import { setCookie } from '@/utils/cookieUtils';

const LoginPage = () => {
  const { t } = useTranslation(['app', 'auth', 'common']);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const showSignUp = location.state?.isSignUp === true;
  const { appName, logoUrl } = useAppSettings();

  useEffect(() => {
    // Check if there's a referral code in the URL
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    if (refCode) {
      // Store the referral code in a cookie for 30 days
      setCookie('referralCode', refCode, 30);
      console.log(`Stored referral code from URL: ${refCode}`);
    }
  }, [location]);

  useEffect(() => {
    // If user is already logged in, redirect to dashboard
    if (user && !loading) {
      console.log('User already logged in, redirecting to dashboard');
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const handleLoginSuccess = () => {
    console.log('Login successful, navigating to dashboard');
    navigate('/dashboard');
  };

  // Add a function to handle logo click
  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#F8F7F5] to-[#F5EBD8]/30 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
          <div className="flex flex-col items-center mb-6">
            <div 
              className="p-4 rounded-full bg-[#E4002B] text-white mb-3 cursor-pointer"
              onClick={handleLogoClick}
            >
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={t('common:logo')} 
                  className="h-10 w-10 object-contain"
                />
              ) : (
                <ChefHat className="h-10 w-10" />
              )}
            </div>
            <h1 
              className="text-4xl font-bold tracking-tight text-[#202124] cursor-pointer" 
              onClick={handleLogoClick}
            >
              {appName}
            </h1>
            <p className="text-muted-foreground mt-2 max-w-sm mx-auto">
              {t('app:welcomeSubtitle')}
            </p>
          </div>
          
          <div className="inline-block mb-3 relative">
            <div className="absolute inset-0 bg-[#E4002B]/10 rounded-full blur-xl animate-pulse-subtle"></div>
            <h2 className="relative text-sm font-medium bg-[#E4002B]/10 text-[#E4002B] px-3 py-1 rounded-full inline-block">
              {showSignUp ? t('auth:joinClub') : t('auth:welcomeBack')}
            </h2>
          </div>
        </div>
        
        <LoginComponent onSuccess={handleLoginSuccess} initialShowSignUp={showSignUp} />
        
        <div className="mt-12 text-center text-xs text-muted-foreground">
          <p>
            {t('auth:agreeToTerms')}{' '}
            <a href="/privacy" className="underline hover:text-foreground transition-colors">
              {t('auth:privacyPolicy')}
            </a>{' '}
            {t('common:and')}{' '}
            <a href="#" className="underline hover:text-foreground transition-colors">
              {t('auth:termsOfService')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
